<script setup lang="ts">
import { computed } from 'vue';
import type { CardLook } from '@vp/shared-types';
import { fillCss, hasFill } from '../card-look';

/**
 * The backdrop of a profile card or of the tray (module API 4.10): the look's background at its
 * opacity, and its motion — a slow gradient, particles, waves or shine. Under the content, never
 * taking the pointer; motion stops for people who asked the system for less of it.
 */
const props = defineProps<{ look: CardLook; base: string }>();
const fill = computed(() => (hasFill(props.look) ? fillCss(props.look, props.base) : null));
const motion = computed(() => props.look.motion ?? 'none');
const PARTICLES = Array.from({ length: 12 }, (_, i) => ({ left: (i * 37) % 100, delay: -((i * 1.7) % 9), size: 4 + ((i * 5) % 7), dur: 7 + ((i * 3) % 6) }));
</script>

<template>
  <span v-if="fill" class="backdrop" :class="'m-' + motion" :style="fill" aria-hidden="true">
    <template v-if="motion === 'particles'">
      <span v-for="(p, i) in PARTICLES" :key="i" class="dot" :style="{ left: p.left + '%', width: p.size + 'px', height: p.size + 'px', animationDelay: p.delay + 's', animationDuration: p.dur + 's' }"></span>
    </template>
    <template v-else-if="motion === 'waves'">
      <span class="wave w1"></span>
      <span class="wave w2"></span>
    </template>
    <span v-else-if="motion === 'shine'" class="shine"></span>
  </span>
</template>

<style scoped>
.backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
  border-radius: inherit;
}
.m-gradient {
  background-size: 300% 300% !important;
  animation: vp-drift 14s ease-in-out infinite alternate;
}
@keyframes vp-drift {
  from {
    background-position: 0% 30%;
  }
  to {
    background-position: 100% 70%;
  }
}
.dot {
  position: absolute;
  bottom: -12px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--card-glint) 70%, transparent);
  animation: vp-rise linear infinite;
}
@keyframes vp-rise {
  from {
    transform: translateY(0);
    opacity: 0;
  }
  15% {
    opacity: 0.9;
  }
  to {
    transform: translateY(-260px);
    opacity: 0;
  }
}
.wave {
  position: absolute;
  left: -50%;
  width: 200%;
  height: 60%;
  bottom: -30%;
  border-radius: 45%;
  background: color-mix(in srgb, var(--card-glint) 22%, transparent);
  animation: vp-wave 9s linear infinite;
}
.w2 {
  bottom: -36%;
  background: color-mix(in srgb, var(--card-glint) 14%, transparent);
  animation-duration: 13s;
  animation-direction: reverse;
}
@keyframes vp-wave {
  to {
    transform: rotate(360deg);
  }
}
.shine {
  position: absolute;
  top: -50%;
  left: -60%;
  width: 40%;
  height: 200%;
  background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--card-glint) 45%, transparent), transparent);
  transform: rotate(20deg);
  animation: vp-shine 5s ease-in-out infinite;
}
@keyframes vp-shine {
  0%,
  55% {
    left: -60%;
  }
  100% {
    left: 130%;
  }
}
@media (prefers-reduced-motion: reduce) {
  .backdrop,
  .backdrop * {
    animation: none !important;
  }
}
</style>
